import { useEffect, useState } from 'react'
import { PortfolioInbox } from '../components/portfolio/PortfolioInbox'
import { PortfolioUpdates } from '../components/portfolio/PortfolioUpdates'
import { PortfolioMemories } from '../components/portfolio/PortfolioMemories'
import { PortfolioGroups } from '../components/portfolio/PortfolioGroups'
import type { PortfolioTabKey } from '../components/portfolio/types'
import { getWSConfig } from '../api/brain'
import { WSClient } from '../lib/ws-client'

const TABS: Array<{ key: PortfolioTabKey; label: string }> = [
  { key: 'inbox', label: 'Inbox' },
  { key: 'updates', label: 'Daily Updates' },
  { key: 'memories', label: 'Memories' },
  { key: 'groups', label: 'Groups' },
]

export function Portfolio() {
  const [activeTab, setActiveTab] = useState<PortfolioTabKey>('inbox')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [inboxRefreshTick, setInboxRefreshTick] = useState(0)
  const [updatesRefreshTick, setUpdatesRefreshTick] = useState(0)
  const [liveConnected, setLiveConnected] = useState(false)
  const [lastLiveEventAt, setLastLiveEventAt] = useState<Date | null>(null)

  const checkDispatchDependency = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch('/api/portfolio/inbox?limit=1', {
        credentials: 'include',
      })
      if (!res.ok) {
        const text = await res.text()
        throw new Error(text || `Portfolio dependency check failed (${res.status})`)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Portfolio dependency check failed'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    checkDispatchDependency()
  }, [])

  useEffect(() => {
    if (loading || error) return

    let disposed = false
    let client: WSClient | null = null
    let unsubOpen: (() => void) | null = null
    let unsubClose: (() => void) | null = null
    let unsubMessage: (() => void) | null = null

    getWSConfig()
      .then(({ wsUrl, token }) => {
        if (disposed) return
        client = new WSClient(wsUrl, token)

        unsubOpen = client.on('open', () => {
          if (disposed) return
          setLiveConnected(true)
          client?.send({ type: 'subscribe_portfolio' })
        })
        unsubClose = client.on('close', () => {
          if (disposed) return
          setLiveConnected(false)
        })
        unsubMessage = client.on('message', (data: Record<string, unknown>) => {
          if (disposed) return
          if (data.type !== 'portfolio_update') return
          const scope = data.scope
          if (scope === 'inbox' || scope === 'all') {
            setInboxRefreshTick((tick) => tick + 1)
          }
          if (scope === 'updates' || scope === 'all') {
            setUpdatesRefreshTick((tick) => tick + 1)
          }
          setLastLiveEventAt(new Date())
        })
        client.connect()
      })
      .catch((err) => {
        if (disposed) return
        console.error('[portfolio] WS config failed:', err)
      })

    return () => {
      disposed = true
      try {
        client?.send({ type: 'unsubscribe_portfolio' })
      } catch {}
      unsubOpen?.()
      unsubClose?.()
      unsubMessage?.()
      client?.disconnect()
      setLiveConnected(false)
    }
  }, [loading, error])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-swiss-body text-swiss-gray-500">Checking portfolio network dependencies...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="border border-swiss-red bg-swiss-off-white p-6">
        <h2 className="text-swiss-label uppercase tracking-widest text-swiss-red mb-3">
          Portfolio Network Error
        </h2>
        <p className="text-swiss-small text-swiss-gray-700 mb-4">
          AgentDispatch is required for portfolio entities to participate in cross-agent communication.
        </p>
        <p className="text-swiss-caption text-swiss-gray-500 mb-4">{error}</p>
        <button
          onClick={checkDispatchDependency}
          className="px-3 py-1.5 text-[13px] border border-swiss-gray-200 text-swiss-black bg-transparent cursor-pointer transition-colors duration-150 hover:border-swiss-black"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-semibold tracking-tight leading-[1.2]">
            Portfolio Command Center
          </h1>
          <p className="text-swiss-gray-500 mt-2 text-[14px]">
            Cross-agent inbox, daily updates, memories, and group communication.
          </p>
        </div>
        <div className="text-right">
          <p className="text-swiss-caption font-swiss-mono text-swiss-gray-400">
            Live {liveConnected ? 'ON' : 'OFF'}
          </p>
          <p className="text-swiss-caption font-swiss-mono text-swiss-gray-400">
            {lastLiveEventAt ? `Last event ${lastLiveEventAt.toLocaleTimeString()}` : 'No events yet'}
          </p>
        </div>
      </div>

      <div className="flex gap-0 border-b border-swiss-gray-200 mb-6" role="tablist" aria-label="Portfolio command center tabs">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.key
          return (
            <button
              key={tab.key}
              role="tab"
              aria-selected={isActive}
              aria-controls="portfolio-tabpanel"
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 text-[13px] font-medium cursor-pointer transition-colors duration-150 border-b-2 -mb-px ${
                isActive
                  ? 'border-swiss-black text-swiss-black'
                  : 'border-transparent text-swiss-gray-500 hover:text-swiss-black'
              }`}
            >
              {tab.label}
            </button>
          )
        })}
      </div>

      <div id="portfolio-tabpanel" role="tabpanel" aria-label={`${activeTab} panel`}>
        {activeTab === 'inbox' && <PortfolioInbox refreshTick={inboxRefreshTick} />}
        {activeTab === 'updates' && <PortfolioUpdates refreshTick={updatesRefreshTick} />}
        {activeTab === 'memories' && <PortfolioMemories />}
        {activeTab === 'groups' && <PortfolioGroups />}
      </div>
    </div>
  )
}
